import { filter, isEmpty } from "lodash/fp";

import { get, patch, post } from "@readai/common/api";
import {
  EnterpriseWorkspace,
  EnterpriseWorkspaceInvite,
  EnterpriseWorkspaceInviteResponse,
  EnterpriseWorkspaceInviteStatus,
  WorkspaceUserInviteBody,
} from "@readai/common/types/workspaceTypes";

import { withQueryParamsUrl } from "../../utils";

interface BulkWorkspaceInviteUpdateBody {
  status: EnterpriseWorkspaceInviteStatus;
  ids: EnterpriseWorkspaceInvite["id"][];
}

interface BulkWorkspaceInviteResult {
  failures?: { errorCode: number; id: EnterpriseWorkspaceInvite["id"] }[];
  invites: EnterpriseWorkspaceInvite[];
}

export const listWorkspaceInvites = async (
  workspaceId: string,
): Promise<EnterpriseWorkspaceInvite[]> =>
  get(`/enterprise/workspaces/${workspaceId}/invites`, {
    withCreds: true,
  });

export const createInvites = async (
  workspaceId: string,
  invites: WorkspaceUserInviteBody[],
): Promise<EnterpriseWorkspaceInviteResponse> => {
  if (isEmpty(invites)) return {};
  return post(`/enterprise/workspaces/${workspaceId}/bulk-invites`, invites, {
    withCreds: true,
    parseErrorResponse: true,
    // 400 status is the status we would receive if one or more invites failed. it will accompany
    // a JSON response detailing which invites succeeded and which failed, which the handler will
    // then parse out to display individual error messages to the user
    parseErrorResponseStatuses: [400],
  });
};

export const updateAllWorkspaceInvites = async (
  workspaceId: EnterpriseWorkspace["id"],
  updateBody: BulkWorkspaceInviteUpdateBody,
): Promise<BulkWorkspaceInviteResult> => {
  return patch(
    `/enterprise/workspaces/${workspaceId}/bulk-invites`,
    updateBody,
    { withCreds: true },
  );
};

/**
 * list workspace invites for the current user
 * */
export const listWorkspaceUserInvites = async (
  status?: EnterpriseWorkspaceInviteStatus,
): Promise<EnterpriseWorkspaceInvite[]> => {
  const path = withQueryParamsUrl("/users/me/workspace_invites", { status });
  const invites: EnterpriseWorkspaceInvite[] = await get(path, {
    withCreds: true,
  });

  return filter((invite) => invite.status !== "removed", invites);
};

/**
 * gets workspace invite for the current user by invite id
 * */
export const getWorkspaceUserInvite = async (
  inviteId: string,
): Promise<EnterpriseWorkspaceInvite> =>
  get(`/users/me/workspace_invites/${inviteId}`, { withCreds: true });

export const updateWorkspaceUserInvite = async ({
  inviteId,
  isAccepted,
}: {
  inviteId: string;
  isAccepted: boolean;
}): Promise<void> =>
  patch(
    `/users/me/workspace_invites/${inviteId}`,
    { status: isAccepted ? "accepted" : "denied" },
    { withCreds: true },
  );
